'use es6';

import { Map as ImmutableMap } from 'immutable';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { once } from 'underscore';
import EPIssueButton from 'editor-components/header/EPIssueButton';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import I18n from 'I18n';
import ContentRoutes from 'ContentUtils/Routes';
import { formatErrorMessage, getErrorLabels } from 'ContentEditorUI/utils/validationUtils';
import LockedPublishButton from 'ContentEditorUI/components/shared/LockedPublishButton';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import cannotPublishOnDomainReasons from 'ContentEditorUI/constants/cannotPublishOnDomainReasons';
import UITour from 'ui-shepherd-react/tour/UITour';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import OneClickPublishTourStep from './OneClickPublishTourStep';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class BaymaxPublishButton extends Component {
  constructor(props) {
    super(props);
    this.handleTrackOnDropdownCompleteAction = () => {
      const {
        trackPublishDropdownAction
      } = this.props;
      if (trackPublishDropdownAction) {
        trackPublishDropdownAction('opened-publish-actions-dropdown');
      }
    };
    this.confirmUnpublish = this.confirmUnpublish.bind(this);
    this.cancelUnpublish = this.cancelUnpublish.bind(this);
    this.unpublishCallback = this.unpublishCallback.bind(this);
    this.errorClickHandler = this.errorClickHandler.bind(this);
    this.sharedPublishCallback = this.sharedPublishCallback.bind(this);
    this.handleMouseEnterButton = this.handleMouseEnterButton.bind(this);
    this.trackHoverPublishWithPermissionError = once(this.trackHoverPublishWithPermissionError.bind(this));
    this.trackHoverPublishWithNoAccessError = once(this.trackHoverPublishWithNoAccessError.bind(this));
    this.unpublishButton = {
      id: 'unpublish-button',
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "header.unpublishButton"
      }),
      onClick: this.confirmUnpublish
    };
    this.state = {
      unpublishConfirmModalOpen: false,
      buttonActions: props.unpublishEnabled ? [...props.actions, this.unpublishButton] : props.actions
    };
  }
  componentDidUpdate(prevProps) {
    const {
      actions,
      unpublishEnabled
    } = this.props;
    const actionsAsArray = actions || [];
    const prevActionsAsArray = prevProps.actions || [];
    const actionsChanged = actionsAsArray.length !== prevActionsAsArray.length;
    if (unpublishEnabled !== prevProps.unpublishEnabled || actionsChanged) {
      if (unpublishEnabled) {
        this.setState({
          buttonActions: [...actionsAsArray, this.unpublishButton]
        });
      } else {
        this.setState({
          buttonActions: actions
        });
      }
    }
  }
  trackHoverPublishWithPermissionError() {
    UsageTracker.trackEditorInteraction({
      action: 'Header Action',
      event: 'hovered-publish-button-with-permission-error'
    });
  }
  trackHoverPublishWithNoAccessError() {
    UsageTracker.trackEditorInteraction({
      action: 'Approvals Action',
      event: 'viewContentEditorAccessRequestTooltip'
    });
  }
  handleMouseEnterButton() {
    const {
      isUserJitad,
      publishEnabled,
      cannotPublishOnDomainReason
    } = this.props;
    if (!publishEnabled) {
      if (cannotPublishOnDomainReason === cannotPublishOnDomainReasons.NO_DOMAIN_PERMISSION) {
        this.trackHoverPublishWithPermissionError();
      } else if (cannotPublishOnDomainReason === cannotPublishOnDomainReasons.USER_HAS_NO_PUBLISH_ACCESS && !isUserJitad) {
        this.trackHoverPublishWithNoAccessError();
      }
    }
  }
  sharedPublishCallback() {
    this.props.publishCallback();
  }
  confirmUnpublish() {
    const {
      trackPublishDropdownAction
    } = this.props;
    if (trackPublishDropdownAction) {
      trackPublishDropdownAction('clicked-action--unpublish');
    }
    this.setState({
      unpublishConfirmModalOpen: true
    });
  }
  cancelUnpublish() {
    this.setState({
      unpublishConfirmModalOpen: false
    });
  }
  unpublishCallback() {
    const {
      unpublish,
      onUnpublishSuccess
    } = this.props;
    unpublish({
      skipPublishRedirect: true,
      onPublishSuccess: () => {
        this.setState({
          unpublishConfirmModalOpen: false
        });
        onUnpublishSuccess();
      }
    });
  }
  errorClickHandler({
    target,
    onClickData
  }) {
    this.props.updateFocusedItem(target, onClickData);
  }
  getCannotPublishOnActiveDomainError() {
    const {
      cannotPublishOnDomainReason,
      contextActiveDomain,
      publishNoun,
      cannotPublishOnDomainReasonMessageOptions
    } = this.props;
    const reasonKey = `publishingOptions.baymaxNotAuthorizedReasons.${cannotPublishOnDomainReason}`;
    const options = Object.assign({
      domain: contextActiveDomain.get('domain'),
      noun: I18n.text(publishNoun),
      domainManagerUrl: ContentRoutes.domainManager(),
      knowledgeArticleUrl: 'https://knowledge.hubspot.com/articles/kcs_article/cos-general/connect-a-domain-to-hubspot'
    }, cannotPublishOnDomainReasonMessageOptions);
    return {
      target: null,
      onClickData: null,
      node: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: reasonKey,
        options: options
      })
    };
  }
  renderUnpublishConfirmModal() {
    const {
      unpublishConfirmModalOpen
    } = this.state;
    if (!unpublishConfirmModalOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIConfirmModal, {
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.message"
      }),
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.description"
      }),
      confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.publishing.unpublish"
      }),
      onConfirm: this.unpublishCallback,
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.cancel"
      }),
      onReject: this.cancelUnpublish,
      size: "auto"
    });
  }
  render() {
    const {
      permanentlyDisabled,
      buttonTextKey,
      hasSaveError,
      cannotPublishOnDomainReason,
      publishEnabled,
      tooltipOverride,
      errors,
      warnings,
      isLocked,
      onClickLocked,
      loading,
      loadingFailed,
      shouldUseOneClickPublish
    } = this.props;
    const {
      tour
    } = this.context;
    const publishErrors = errors.map(formatErrorMessage);
    if (cannotPublishOnDomainReason) {
      publishErrors.push(this.getCannotPublishOnActiveDomainError());
    }
    const publishWarnings = getErrorLabels(warnings).map(formatErrorMessage);
    const renderedButton = /*#__PURE__*/_jsx("div", {
      "data-onboarding": "onboarding-publish-button",
      "data-campaign-assistant-onboarding-tour": "tour-step-publish",
      children: /*#__PURE__*/_jsx(EPIssueButton, {
        actions: this.state.buttonActions,
        "data-test": "publish-issue-button",
        errors: publishErrors,
        warnings: publishWarnings,
        tooltipOverride: tooltipOverride,
        usageTracker: UsageTracker.getCachedClonedTracker('editorComponents'),
        onButtonClick: this.sharedPublishCallback,
        onDropdownOpenComplete: this.handleTrackOnDropdownCompleteAction,
        onIssueClick: this.errorClickHandler,
        onMouseEnter: this.handleMouseEnterButton,
        disabled: !publishEnabled || permanentlyDisabled || hasSaveError,
        loading: loading,
        loadingFailed: loadingFailed,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: buttonTextKey
        })
      })
    });
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [this.renderUnpublishConfirmModal(), /*#__PURE__*/_jsx(UITour, {
        tour: "cms-editor-one-click-publish",
        activateOnMount: false,
        config: {
          steps: ['oneClickPublishClicked']
        },
        children: isLocked ? /*#__PURE__*/_jsx(LockedPublishButton, {
          actions: this.state.buttonActions,
          onClick: onClickLocked,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: buttonTextKey
          })
        }) : shouldUseOneClickPublish && tour && tour.getStep() === 'oneClickPublishClicked' ? /*#__PURE__*/_jsx(OneClickPublishTourStep, {
          children: renderedButton
        }) : renderedButton
      })]
    });
  }
}
BaymaxPublishButton.contextType = TourContext;
BaymaxPublishButton.propTypes = {
  contextActiveDomain: PropTypes.instanceOf(ImmutableMap),
  permanentlyDisabled: PropTypes.bool.isRequired,
  publishNoun: PropTypes.string.isRequired,
  buttonTextKey: PropTypes.string.isRequired,
  tooltipOverride: PropTypes.node,
  updateFocusedItem: PropTypes.func.isRequired,
  hasSaveError: PropTypes.bool.isRequired,
  cannotPublishOnDomainReason: PropTypes.string,
  cannotPublishOnDomainReasonMessageOptions: PropTypes.object.isRequired,
  unpublish: PropTypes.func.isRequired,
  publishEnabled: PropTypes.bool,
  unpublishEnabled: PropTypes.bool,
  publishCallback: PropTypes.func.isRequired,
  onUnpublishSuccess: PropTypes.func.isRequired,
  isUserJitad: PropTypes.bool.isRequired,
  actions: PropTypes.array,
  isLocked: PropTypes.bool.isRequired,
  onClickLocked: PropTypes.func,
  loading: PropTypes.bool,
  loadingFailed: PropTypes.bool,
  shouldUseOneClickPublish: PropTypes.bool.isRequired,
  trackPublishDropdownAction: PropTypes.func,
  errors: PropTypes.array.isRequired,
  warnings: PropTypes.array.isRequired
};
BaymaxPublishButton.defaultProps = {
  settingsWarnings: {},
  cannotPublishOnDomainReasonMessageOptions: {},
  personalizationTokensWithoutAccessError: {},
  isLocked: false,
  shouldUseOneClickPublish: false
};